/**
 * Client ของการรับเข้าสต๊อก — พูดกับ `/v1/goods-receipts` ตาม payload contract
 * ที่ freeze ไว้ใน `docs/API_CONTRACT.md`
 *
 * หลักการเดียวกับ `lib/productionOrders/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ยังไม่มีใบรับเข้า" — รายการว่าง
 *   กับโหลดไม่สำเร็จต้องแยกกันให้ขาด
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 *   (D-049 อนุญาตเฉพาะ session token) — และที่นี่สำคัญเป็นพิเศษ: สต๊อกที่ cache
 *   ไว้คือของที่อาจถูกเบิกไปแล้ว หรือยังกักอยู่ทั้งที่หน้าจอบอกว่าปล่อยแล้ว
 *
 * **สิ่งที่ไฟล์นี้ไม่มีโดยเจตนา: `quality-decision`** —
 * `POST /v1/lots/{lot_id}/quality-decision` เป็นงานของ `WO-P1-M4B-002` และยัง
 * ไม่มีอยู่จริง การเขียนฟังก์ชันไว้ก่อนจะได้หน้าจอที่ยิงไปชน `404` แล้วรายงานเป็น
 * "เซิร์ฟเวอร์ผิดพลาด" ซึ่งผู้ใช้ทำอะไรต่อไม่ได้เลย — และที่แย่กว่านั้นคือปุ่ม
 * "ปล่อยผ่าน" ที่กดแล้วไม่เกิดอะไร จะสอนว่าของชุดนั้นเบิกได้แล้ว
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import {
  IDEMPOTENCY_KEY_HEADER,
  type CreateGoodsReceiptBody,
  type GoodsReceipt,
  type GoodsReceiptListFilters,
  type GoodsReceiptListPage,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ
 */
export const GOODS_RECEIPTS_PATH = '/v1/goods-receipts';

export function goodsReceiptPath(receiptId: string): string {
  return `${GOODS_RECEIPTS_PATH}/${encodeURIComponent(receiptId)}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ หน้าจอต้องแสดงค่านี้ ไม่ใช่กลืนเป็น "ผิดพลาด" ทั่วไป
   * `NO_UNIT_COST` กับ `NO_ACCOUNT_MAPPING` ต่างกันแค่ค่านี้ค่าเดียว และคนละคน
   * เป็นผู้แก้
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * ไม่ยุบ 403/404/409/422 เข้าด้วยกันโดยเจตนา: `403` ของกลุ่มนี้แปลว่า **Plant นี้
 * อยู่นอกขอบเขตของคุณ** (D-004 — contract ระบุว่านอก scope ตอบ `403` ไม่ใช่ `404`)
 * ส่วน `409` ของกลุ่มนี้มีความหมายเดียวคือ `IDEMPOTENCY_KEY_REUSED` ซึ่งเป็น bug
 * ของ client ไม่ใช่การชนกันของผู้ใช้สองคน — คนละเรื่องและต้องทำต่างกัน
 */
export type GoodsReceiptFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type GoodsReceiptListResult =
  | { readonly status: 'ready'; readonly page: GoodsReceiptListPage }
  | GoodsReceiptFailure;

export type GoodsReceiptResult =
  | { readonly status: 'ready'; readonly receipt: GoodsReceipt }
  | GoodsReceiptFailure;

export interface GoodsReceiptRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* --------------------------------------------------------------- query ---- */

/**
 * ต่อ query string ตามลำดับที่ contract เขียนไว้
 *
 * ⚠️ `plant_code` ที่นี่เป็น **ตัวกรองที่ไม่บังคับ** ต่างจากบน `POST` — ค่าที่เป็น
 * สตริงว่างถูกตัดทิ้ง เพราะ `?plant_code=` ไม่ได้แปลว่า "โรงงานที่ไม่มีชื่อ" แต่แปลว่า
 * ผู้ใช้ยังไม่ได้เลือก และการส่งมันไปจะทำให้ผู้ที่ถือหลายโรงงานดูรวมไม่ได้
 */
export function buildGoodsReceiptsQuery(filters: GoodsReceiptListFilters = {}): string {
  const params = new URLSearchParams();
  const append = (key: string, value: string | undefined) => {
    if (typeof value === 'string' && value.trim() !== '') {
      params.append(key, value.trim());
    }
  };

  append('plant_code', filters.plant_code);
  append('product_code', filters.product_code);
  append('storage_location_code', filters.storage_location_code);
  if (typeof filters.limit === 'number' && Number.isInteger(filters.limit)) {
    params.append('limit', String(filters.limit));
  }
  append('cursor', filters.cursor);

  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * อ่านใบรับเข้าหนึ่งใบ — **บังคับครบทุกฟิลด์ที่ contract บังคับ**
 *
 * ต่างจาก `parseProductionOrder()` ที่ปล่อยเกือบทุกฟิลด์เป็น nullable เพราะคำตอบ
 * ของกลุ่มนั้นไม่เคยถูก freeze ที่นี่ contract freeze รูปร่างไว้แล้ว **โดยถอดจาก
 * โค้ดที่ `WO-P1-M4B-001` สร้างจริง** การผ่อนให้หลวมกว่านั้นจึงไม่ใช่ความระมัดระวัง
 * แต่เป็นการเปิดทางให้ใบที่ขาด `quality_status` หรือขาด `lot_id` แสดงผลได้ —
 * ใบที่ไม่บอกว่าของยังถูกกักอยู่ คือใบที่บอกผิดในเรื่องเดียวที่สำคัญที่สุด
 *
 * `supplier_lot_id` เป็นฟิลด์เดียวที่ `null` ได้ ตามที่ contract ระบุไว้ตรง ๆ
 */
export function parseGoodsReceipt(raw: unknown): GoodsReceipt | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const receiptId = requiredString(record.receipt_id);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const storageLocationId = requiredString(record.storage_location_id);
  const storageLocationCode = requiredString(record.storage_location_code);
  const lotId = requiredString(record.lot_id);
  const qualityStatus = requiredString(record.quality_status);
  // ⚠️ `quantity` ผ่าน `requiredString` ไม่ใช่ตัวแปลงตัวเลข: `NUMERIC(18,4)` ที่
  // ผ่าน `Number` มาแล้วคือค่าที่เพี้ยนไปก่อนถึงที่นี่ การรับไว้คือการกลืนมัน
  const quantity = requiredString(record.quantity);
  const postingDate = requiredString(record.posting_date);
  const createdBy = requiredString(record.created_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    receiptId === null ||
    plantId === null ||
    plantCode === null ||
    productId === null ||
    productCode === null ||
    storageLocationId === null ||
    storageLocationCode === null ||
    lotId === null ||
    qualityStatus === null ||
    quantity === null ||
    postingDate === null ||
    createdBy === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const supplierLotId = nullableString(record.supplier_lot_id);
  if (supplierLotId === undefined) {
    return null;
  }

  return {
    receipt_id: receiptId,
    plant_id: plantId,
    plant_code: plantCode,
    product_id: productId,
    product_code: productCode,
    storage_location_id: storageLocationId,
    storage_location_code: storageLocationCode,
    lot_id: lotId,
    quality_status: qualityStatus,
    supplier_lot_id: supplierLotId,
    quantity,
    posting_date: postingDate,
    created_by_employee_id: createdBy,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * อ่านรายการจาก envelope `{ items, next_cursor }` — **และจากที่นั่นเท่านั้น**
 *
 * contract freeze รูปนี้ไว้ตรง ๆ สำหรับกลุ่มนี้ จึงไม่รับ array เปล่าเป็นทางถอย
 * อย่างที่ `parseProductionOrderListPage()` ต้องทำตอนที่ envelope ของกลุ่มนั้นยัง
 * ไม่ถูก freeze — parser ที่เขียนขึ้นจากรูปที่เดาเอาเองคือ defect ที่ทำให้หน้าจอ
 * BOM เคยขึ้นว่า "ยังไม่มีสูตร" ให้สินค้าที่มีสูตรอยู่แล้ว
 */
export function parseGoodsReceiptListPage(raw: unknown): GoodsReceiptListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: GoodsReceipt[] = [];
  for (const entry of record.items) {
    const parsed = parseGoodsReceipt(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = nullableString(record.next_cursor);
  if (cursor === undefined) {
    return null;
  }
  // cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่าง
  // จะวนขอหน้าเดิมซ้ำไปเรื่อย ๆ
  return { items, next_cursor: cursor === null || cursor.trim() === '' ? null : cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | GoodsReceiptFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: GoodsReceiptRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** `GET /v1/goods-receipts` — list ที่ถูกจำกัดด้วย plant scope ของผู้เรียกเสมอ */
export async function fetchGoodsReceipts(
  filters: GoodsReceiptListFilters = {},
  options: GoodsReceiptRequestOptions = {},
): Promise<GoodsReceiptListResult> {
  const result = await requestPayload(
    `${GOODS_RECEIPTS_PATH}${buildGoodsReceiptsQuery(filters)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseGoodsReceiptListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `GET /v1/goods-receipts/{receipt_id}` — ใบเดียว พร้อม lot ที่มันสร้าง */
export async function fetchGoodsReceipt(
  receiptId: string,
  options: GoodsReceiptRequestOptions = {},
): Promise<GoodsReceiptResult> {
  const result = await requestPayload(
    goodsReceiptPath(receiptId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const receipt = parseGoodsReceipt(result.payload);
  return receipt === null ? { status: 'invalid-response' } : { status: 'ready', receipt };
}

/**
 * แปลง body ให้เหลือเฉพาะหกคีย์ที่ contract รับ — และไม่มีคีย์ที่เจ็ด
 *
 * ด่านที่สองต่อจากชนิดข้อมูล: ต่อให้ object ที่ส่งเข้ามามีคีย์แปลกปลอมติดมา (เช่น
 * state ของฟอร์มที่ถูก spread ทั้งก้อน) สิ่งที่ออกไปกับ request ยังเป็นเฉพาะคีย์ที่นี่
 * **`quality_status` จึงไปถึงเซิร์ฟเวอร์ไม่ได้เลย** ซึ่งเป็นเงื่อนไขที่ทำให้การกักกัน
 * ของ D-059 ยังมีความหมาย — เขียนคีย์ทีละตัวด้วยเหตุผลเดียวกับ
 * `serializeCreateProductionOrderBody()`
 *
 * ⚠️ `quantity` ถูกเขียนลงไปเป็นค่าที่รับมาตรง ๆ ซึ่งเป็น `string` ตามชนิดข้อมูล
 * ไม่มีจุดใดในเส้นทางนี้ที่มันผ่าน `Number` — `JSON.stringify` จึงเขียนมันออกไป
 * เป็นสตริงใน JSON ไม่ใช่ตัวเลข (API ปฏิเสธตัวเลขตรง ๆ)
 */
export function serializeCreateGoodsReceiptBody(
  body: CreateGoodsReceiptBody,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    product_code: body.product_code,
    storage_location_code: body.storage_location_code,
    quantity: body.quantity,
    posting_date: body.posting_date,
    supplier_lot_id: body.supplier_lot_id,
  };
}

/**
 * `POST /v1/goods-receipts` — ของเข้าสต๊อกจริง ห้าแถวในธุรกรรมเดียว
 *
 * `idempotencyKey` เป็น parameter **บังคับ** โดยเจตนา ไม่ใช่ค่าที่ฟังก์ชันนี้สร้าง
 * ให้เอง: คีย์ที่สร้างขึ้นใหม่ทุกครั้งที่ถูกเรียกคือคีย์ที่ไม่ได้กันอะไรเลย — การ
 * กดสองครั้งจะได้ lot สองใบ ซึ่งตรงกับสิ่งที่ `request_dedup` มีไว้ป้องกันพอดี
 * ผู้เรียกต้องเป็นผู้ตัดสินว่า "นี่คือการส่งซ้ำของใบเดิม" หรือ "นี่คือใบใหม่"
 * (ดู `idempotency.ts`)
 */
export async function createGoodsReceipt(
  body: CreateGoodsReceiptBody,
  idempotencyKey: string,
  options: GoodsReceiptRequestOptions = {},
): Promise<GoodsReceiptResult> {
  const result = await requestPayload(
    GOODS_RECEIPTS_PATH,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        [IDEMPOTENCY_KEY_HEADER]: idempotencyKey,
      },
      body: JSON.stringify(serializeCreateGoodsReceiptBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const receipt = parseGoodsReceipt(result.payload);
  return receipt === null ? { status: 'invalid-response' } : { status: 'ready', receipt };
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย
 */
export function ruleFromFailure(failure: GoodsReceiptFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}
